import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { SceneOverrides, SceneLight } from '@/worker/types';

export interface MaterialSettings {
  metalness: number;
  roughness: number;
  envMapIntensity: number;
  flatShading: boolean;
  // Physical material mode
  usePhysicalMaterial: boolean;
  // Physical material properties (only active when usePhysicalMaterial is true)
  clearcoat: number;
  clearcoatRoughness: number;
  sheen: number;
  sheenRoughness: number;
  sheenColor: [number, number, number];
  iridescence: number;
  iridescenceIOR: number;
  transmission: number;
  ior: number;
  thickness: number;
  attenuationColor: [number, number, number];
  attenuationDistance: number;
}

export interface LightSettings {
  ambientIntensity: number;
  keyLightIntensity: number;
  fillLightIntensity: number;
  rimLightIntensity: number;
}

export interface ShadowSettings {
  enabled: boolean;
  soft: boolean;
  intensity: number;
  mapSize: number; // 512, 1024, 2048, 4096
}

export interface SSAOSettings {
  enabled: boolean;
  intensity: number; // 0-4, controls strength of the occlusion
  aoRadius: number; // 0-2, controls the spread/size of the occlusion
  samples: number; // 8, 16, 24, 32 — quality vs performance
  luminanceInfluence: number; // 0-1, how much surface brightness affects AO
}

export interface BloomSettings {
  enabled: boolean;
  intensity: number; // 0-5, overall bloom strength
  luminanceThreshold: number; // 0-1, brightness threshold for bloom (lower = more bloom)
  luminanceSmoothing: number; // 0-1, smoothness of the threshold cutoff
  mipmapBlur: boolean; // use mipmap blur for smoother, more physically accurate bloom
}

export interface SceneSettings {
  material: MaterialSettings;
  light: LightSettings;
  shadow: ShadowSettings;
  ssao: SSAOSettings;
  bloom: BloomSettings;
}

const defaultSettings: SceneSettings = {
  material: {
    metalness: 0.0,
    roughness: 0.0,
    envMapIntensity: 0.1,
    flatShading: false,
    usePhysicalMaterial: false,
    clearcoat: 0.0,
    clearcoatRoughness: 0.0,
    sheen: 0.0,
    sheenRoughness: 0.0,
    sheenColor: [1, 1, 1],
    iridescence: 0.0,
    iridescenceIOR: 1.3,
    transmission: 0.0,
    ior: 1.5,
    thickness: 0.0,
    attenuationColor: [1, 1, 1],
    attenuationDistance: 0.0,
  },
  light: {
    ambientIntensity: 1.9,
    keyLightIntensity: 1.61,
    fillLightIntensity: 0.5,
    rimLightIntensity: 0.16,
  },
  shadow: {
    enabled: true,
    soft: true,
    intensity: 0.3,
    mapSize: 2048,
  },
  ssao: {
    enabled: false,
    intensity: 4,
    aoRadius: 2,
    samples: 32,
    luminanceInfluence: 1,
  },
  bloom: {
    enabled: true,
    intensity: 1.0,
    luminanceThreshold: 0.9,
    luminanceSmoothing: 0.025,
    mipmapBlur: true,
  },
};

type SceneSettingsContextType = {
  settings: SceneSettings;
  setSettings: (settings: SceneSettings) => void;
  updateMaterial: (material: Partial<MaterialSettings>) => void;
  updateLight: (light: Partial<LightSettings>) => void;
  updateShadow: (shadow: Partial<ShadowSettings>) => void;
  updateSSAO: (ssao: Partial<SSAOSettings>) => void;
  updateBloom: (bloom: Partial<BloomSettings>) => void;
  performanceMode: boolean;
  togglePerformanceMode: () => void;
  resetToDefaults: () => void;
  /** Overrides detected from scene_* module calls in the current SCAD source. */
  sceneOverrides: SceneOverrides;
  setSceneOverrides: (overrides: SceneOverrides) => void;
  /** Custom lights detected from scene_light() calls in the current SCAD source. */
  sceneLights: SceneLight[];
  setSceneLights: (lights: SceneLight[]) => void;
  /**
   * The effective settings after merging sceneOverrides on top of the UI
   * settings. This is what Three.js components should consume.
   * Fields present in sceneOverrides take priority; the UI value is used
   * as the fallback for everything else.
   */
  mergedSettings: SceneSettings;
};

const SceneSettingsContext = createContext<SceneSettingsContextType | null>(
  null,
);

export function SceneSettingsProvider({
  children,
  initialPerformanceMode = false,
}: {
  children: ReactNode;
  initialPerformanceMode?: boolean;
}) {
  const [settings, setSettings] = useState<SceneSettings>(defaultSettings);
  const [sceneOverrides, setSceneOverrides] = useState<SceneOverrides>({});
  const [sceneLights, setSceneLights] = useState<SceneLight[]>([]);
  // Use lazy initializer so the initial value is captured once and never
  // re-derived from the prop — toggled independently by consumers thereafter.
  const [performanceMode, setPerformanceMode] = useState(
    () => initialPerformanceMode,
  );

  const togglePerformanceMode = useCallback(() => {
    setPerformanceMode((prev) => !prev);
  }, []);

  const updateMaterial = useCallback((material: Partial<MaterialSettings>) => {
    setSettings((prev) => ({
      ...prev,
      material: { ...prev.material, ...material },
    }));
  }, []);

  const updateLight = useCallback((light: Partial<LightSettings>) => {
    setSettings((prev) => ({
      ...prev,
      light: { ...prev.light, ...light },
    }));
  }, []);

  const updateShadow = useCallback((shadow: Partial<ShadowSettings>) => {
    setSettings((prev) => ({
      ...prev,
      shadow: { ...prev.shadow, ...shadow },
    }));
  }, []);

  const updateSSAO = useCallback((ssao: Partial<SSAOSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ssao: { ...prev.ssao, ...ssao },
    }));
  }, []);

  const updateBloom = useCallback((bloom: Partial<BloomSettings>) => {
    setSettings((prev) => ({
      ...prev,
      bloom: { ...prev.bloom, ...bloom },
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(defaultSettings);
  }, []);

  /**
   * Merge sceneOverrides on top of the UI settings, field by field.
   * Only explicitly-set override fields win; everything else falls back to
   * the current UI value.
   */
  const mergedSettings = useMemo<SceneSettings>(() => {
    const o = sceneOverrides;

    // Material
    const om = o.material;
    const material: MaterialSettings = om
      ? {
          ...settings.material,
          ...(om.metalness !== undefined && { metalness: om.metalness }),
          ...(om.roughness !== undefined && { roughness: om.roughness }),
          ...(om.envMapIntensity !== undefined && {
            envMapIntensity: om.envMapIntensity,
          }),
          ...(om.flatShading !== undefined && { flatShading: om.flatShading }),
          ...(om.usePhysicalMaterial !== undefined && {
            usePhysicalMaterial: om.usePhysicalMaterial,
          }),
          ...(om.clearcoat !== undefined && { clearcoat: om.clearcoat }),
          ...(om.clearcoatRoughness !== undefined && {
            clearcoatRoughness: om.clearcoatRoughness,
          }),
          ...(om.sheen !== undefined && { sheen: om.sheen }),
          ...(om.sheenRoughness !== undefined && {
            sheenRoughness: om.sheenRoughness,
          }),
          ...(om.sheenColor !== undefined && { sheenColor: om.sheenColor }),
          ...(om.iridescence !== undefined && { iridescence: om.iridescence }),
          ...(om.iridescenceIOR !== undefined && {
            iridescenceIOR: om.iridescenceIOR,
          }),
          ...(om.transmission !== undefined && {
            transmission: om.transmission,
          }),
          ...(om.ior !== undefined && { ior: om.ior }),
          ...(om.thickness !== undefined && { thickness: om.thickness }),
          ...(om.attenuationColor !== undefined && {
            attenuationColor: om.attenuationColor,
          }),
          ...(om.attenuationDistance !== undefined && {
            attenuationDistance: om.attenuationDistance,
          }),
        }
      : settings.material;

    // Default lights (the built-in three-point rig)
    const odl = o.defaultLights;
    const light: LightSettings = odl
      ? {
          ...settings.light,
          ...(odl.ambient !== undefined && { ambientIntensity: odl.ambient }),
          ...(odl.key !== undefined && { keyLightIntensity: odl.key }),
          ...(odl.fill !== undefined && { fillLightIntensity: odl.fill }),
          ...(odl.rim !== undefined && { rimLightIntensity: odl.rim }),
        }
      : settings.light;

    // Shadows
    const osh = o.shadows;
    const shadow: ShadowSettings = osh
      ? {
          ...settings.shadow,
          ...(osh.enabled !== undefined && { enabled: osh.enabled }),
          ...(osh.soft !== undefined && { soft: osh.soft }),
          ...(osh.intensity !== undefined && { intensity: osh.intensity }),
          ...(osh.mapSize !== undefined && { mapSize: osh.mapSize }),
        }
      : settings.shadow;

    // Bloom
    const obl = o.bloom;
    const bloom: BloomSettings = obl
      ? {
          ...settings.bloom,
          ...(obl.enabled !== undefined && { enabled: obl.enabled }),
          ...(obl.intensity !== undefined && { intensity: obl.intensity }),
          ...(obl.luminanceThreshold !== undefined && {
            luminanceThreshold: obl.luminanceThreshold,
          }),
          ...(obl.luminanceSmoothing !== undefined && {
            luminanceSmoothing: obl.luminanceSmoothing,
          }),
          ...(obl.mipmapBlur !== undefined && { mipmapBlur: obl.mipmapBlur }),
        }
      : settings.bloom;

    // SSAO
    const oao = o.ssao;
    const ssao: SSAOSettings = oao
      ? {
          ...settings.ssao,
          ...(oao.enabled !== undefined && { enabled: oao.enabled }),
          ...(oao.intensity !== undefined && { intensity: oao.intensity }),
          ...(oao.aoRadius !== undefined && { aoRadius: oao.aoRadius }),
          ...(oao.samples !== undefined && { samples: oao.samples }),
          ...(oao.luminanceInfluence !== undefined && {
            luminanceInfluence: oao.luminanceInfluence,
          }),
        }
      : settings.ssao;

    return { material, light, shadow, bloom, ssao };
  }, [settings, sceneOverrides]);

  const value = useMemo(
    () => ({
      settings,
      setSettings,
      updateMaterial,
      updateLight,
      updateShadow,
      updateSSAO,
      updateBloom,
      performanceMode,
      togglePerformanceMode,
      resetToDefaults,
      sceneOverrides,
      setSceneOverrides,
      sceneLights,
      setSceneLights,
      mergedSettings,
    }),
    [
      settings,
      updateMaterial,
      updateLight,
      updateShadow,
      updateSSAO,
      updateBloom,
      performanceMode,
      togglePerformanceMode,
      resetToDefaults,
      sceneOverrides,
      sceneLights,
      mergedSettings,
    ],
  );

  return (
    <SceneSettingsContext.Provider value={value}>
      {children}
    </SceneSettingsContext.Provider>
  );
}

export function useSceneSettings() {
  const context = useContext(SceneSettingsContext);
  if (!context) {
    throw new Error(
      'useSceneSettings must be used within a SceneSettingsProvider',
    );
  }
  return context;
}

export { defaultSettings as sceneSettingsDefaults };
